<template>
  <ContentWrapper>

    <!-- Slider Container -->
    <div class="relative overflow-hidden">
      <div class="flex transition-transform duration-500 ease-in-out"
        :style="{ transform: `translateX(-${currentIndex * 100}%)` }">
        <div v-for="(testimonial, index) in testimonials" :key="index" class="w-full flex-shrink-0 px-4">
          <div class="bg-white/80 rounded-tl-4xl rounded-br-4xl p-8 md:p-12 max-w-4xl mx-auto">
            <!-- Quote Icon -->
            <div class="flex justify-center mb-6">
              <svg class="w-12 h-12 text-primary-500 opacity-50" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>
            </div>

            <!-- Testimonial Text -->
            <p class="text-gray-800 text-lg md:text-xl text-center mb-8 leading-relaxed italic">
              "{{ testimonial.quote }}"
            </p>

            <!-- Author Info -->
            <div class="flex flex-col md:flex-row items-center justify-center gap-6">
              <!-- Company Logo -->
              <div class="h-16 flex items-center justify-center px-4">
                <img :src="testimonial.logo" :alt="testimonial.company"
                  class="max-h-full max-w-[140px] object-contain opacity-80 hover:opacity-100 transition-opacity" />
              </div>

              <!-- Name and Role -->
              <div class="text-center md:text-left">
                <h4 class="font-bold text-secondary-500 text-lg">{{ testimonial.name }}</h4>
                <p v-if="testimonial.role" class="text-gray-600 text-sm">{{ testimonial.role }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation Arrows -->
      <button @click="prevSlide"
        class="absolute left-0 top-1/2 -translate-y-1/2 bg-white hover:bg-primary-50 text-accent-500 rounded-full p-3 shadow-lg transition-all hover:text-accept-600 cursor-pointer z-10"
        aria-label="Previous testimonial">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <button @click="nextSlide"
        class="absolute right-0 top-1/2 -translate-y-1/2 bg-white hover:bg-primary-50 text-accent-500 rounded-full p-3 shadow-lg transition-all hover:text-accept-600 cursor-pointer z-10"
        aria-label="Next testimonial">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>

    <!-- Dots Navigation -->
    <div class="flex justify-center gap-2 mt-8">
      <button v-for="(testimonial, index) in testimonials" :key="index" @click="goToSlide(index)" :class="[
        'w-3 h-3 rounded-full transition-all duration-300',
        currentIndex === index
          ? 'bg-primary-500 w-8'
          : 'bg-primary-200 hover:bg-secondary-400'
      ]" :aria-label="`Go to testimonial ${index + 1}`" />
    </div>
  </ContentWrapper>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import ContentWrapper from '@/components/Layouts/ContentWrapper.vue'
import { partnerTestimonials } from '@/data/partnerTestimonials'

// Testimonials data
const testimonials = ref([...partnerTestimonials])

// Current slide index
const currentIndex = ref(0)

// Auto-play interval
let autoPlayInterval = null

// Navigation methods
const nextSlide = () => {
  currentIndex.value = (currentIndex.value + 1) % testimonials.value.length
}

const prevSlide = () => {
  currentIndex.value = currentIndex.value === 0
    ? testimonials.value.length - 1
    : currentIndex.value - 1
}

const goToSlide = (index) => {
  currentIndex.value = index
}

// Auto-play functionality
const startAutoPlay = () => {
  autoPlayInterval = setInterval(() => {
    nextSlide()
  }, 5000) // Change slide every 5 seconds
}

const stopAutoPlay = () => {
  if (autoPlayInterval) {
    clearInterval(autoPlayInterval)
  }
}

// Lifecycle hooks
onMounted(() => {
  startAutoPlay()
})

onUnmounted(() => {
  stopAutoPlay()
})
</script>

<style scoped>
.text-gradient {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.glass-effect {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.15);
}
</style>
